import {createElement} from 'react';

import classNames from 'classnames';

import type {ComponentPropsWithoutRef, ElementType, ReactElement, ReactNode} from 'react';

export type ItemProps<T extends ElementType = 'div'> = {
    /**
     * Polymorphic element type for this item. Defaults to `"div"`.
     */
    as?: T,
    /**
     * Content rendered inside the item element.
     */
    children?: ReactNode,
    /**
     * When `true`, applies `visibility: hidden` so the item takes up layout space
     * but is invisible. Useful for maintaining alignment when toggling item
     * visibility without reflowing siblings.
     */
    hidden?: boolean,
    /**
     * CSS `flex-grow` factor. Controls how much of the remaining space in the
     * `<Group>` container this item claims relative to siblings. `1` makes the
     * item expand to fill available space; `0` (default) keeps it at its natural size.
     */
    grow?: number,
    /**
     * CSS `flex-shrink` factor. Controls how much this item compresses relative to
     * siblings when the container is too small. `0` prevents shrinking entirely.
     */
    shrink?: number,
    /**
     * CSS `order` — overrides the source-order position of this item within the
     * flex container without reordering the DOM.
     */
    order?: number,
    /**
     * CSS `flex-basis` — the initial main-axis size before remaining space is
     * distributed. Pass `"auto"` (default) to use the item's natural size, or any
     * valid CSS length/percentage (e.g. `"50%"`, `"200px"`).
     */
    basis?: 'auto' | string,
    /**
     * CSS `align-self` — overrides the parent `<Group>` `alignItems` for just this
     * item, allowing individual cross-axis alignment control.
     */
    align?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch' | 'start' | 'end' | 'self-start' | 'self-end' | 'normal',
} & ComponentPropsWithoutRef<T>;

/**
 * Per-item flex control element for use as a direct child of `<Group>`.
 *
 * Applies `flex-grow`, `flex-shrink`, `flex-basis`, `order`, and `align-self`
 * via CSS custom properties so the parent `<Group>` layout can be fine-tuned
 * at the item level. All other native element props are forwarded to the rendered
 * element (polymorphic via `as`).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function Item<T extends ElementType = 'div'>(props: ItemProps<T>): ReactElement<any> {
    const {
        as = 'div',
        hidden = false,
        grow,
        shrink,
        order,
        basis,
        align,
        ...otherProps
    } = props;

    const itemClass = classNames('group__i', otherProps.className, {
        group__i_hid: hidden,
    });

    const itemStyle = {
        '--gr__i-g': grow,
        '--gr__i-s': shrink,
        '--gr__i-or': order,
        '--gr__i-bs': basis,
        '--gr__i-as': align,
        ...otherProps.style,
    };

    return createElement(as, {
        ...otherProps,
        style: itemStyle,
        className: itemClass,
    }, props.children);
}
